/**
 * DocumentPreviewModal - Full document viewer with metadata for internal review
 * 
 * Features:
 * - Full document preview (PDF in iframe)
 * - Document metadata display: order_ref, service_code, version, status, generated_at, generated_by, hash
 * - Version history (clickable v1/v2/v3)
 * - Download buttons for DOCX (editable) and PDF (delivery)
 * - Review checkbox gate before approval
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import { Label } from '../../ui/label';
import { ScrollArea } from '../../ui/scroll-area';
import { Separator } from '../../ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip';
import {
  FileText,
  Download,
  Lock,
  Eye,
  Copy,
  Check,
  Clock,
  User,
  Hash,
  AlertCircle,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { toast } from '@/utils/portalNotifications';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Format date for display
 */
const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format prompt_version_used for display (dict with template_id/version or string)
 */
const formatPromptVersion = (pv) => {
  if (pv == null) return '-';
  if (typeof pv === 'string') return pv;
  if (typeof pv === 'object' && pv !== null) {
    const tid = pv.template_id ?? pv.templateId ?? '';
    const ver = pv.version != null ? pv.version : '';
    return tid ? (ver !== '' ? `${tid} v${ver}` : tid) : JSON.stringify(pv);
  }
  return String(pv);
};

/**
 * Copy text to clipboard with toast notification
 */
const copyToClipboard = (text, label = 'Value') => {
  navigator.clipboard.writeText(text);
  toast.success(`${label} copied to clipboard`);
};

/**
 * Version status badge component
 */
const VersionStatusBadge = ({ status }) => {
  const styles = {
    FINAL: 'bg-green-100 text-green-800 border-green-200',
    DRAFT: 'bg-amber-100 text-amber-800 border-amber-200',
    REGENERATED: 'bg-blue-100 text-blue-800 border-blue-200',
    SUPERSEDED: 'bg-gray-100 text-gray-600 border-gray-200',
  };

  return (
    <Badge className={cn('text-xs', styles[status] || 'bg-gray-100 text-gray-600')}>
      {status}
    </Badge>
  );
};

/**
 * Single metadata row component
 */
const MetadataRow = ({ label, value, copyable = false, icon: Icon, onCopy, copiedField }) => (
  <div className="flex items-center justify-between py-1.5 text-sm">
    <span className="text-gray-500 flex items-center gap-2">
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {label}
    </span>
    <div className="flex items-center gap-2">
      <span className="font-mono text-xs">{value || '-'}</span>
      {copyable && value && onCopy && (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0"
          onClick={() => onCopy(value, label)}
        >
          {copiedField === label ? (
            <Check className="h-3 w-3 text-green-600" />
          ) : (
            <Copy className="h-3 w-3 text-gray-400" />
          )}
        </Button>
      )}
    </div>
  </div>
);

/**
 * Document metadata display
 */
const DocumentMetadata = ({ version, orderId, serviceCode }) => {
  const [copiedField, setCopiedField] = useState(null);

  const handleCopy = (value, field) => {
    copyToClipboard(value, field);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div className="bg-gray-50 rounded-lg p-3 space-y-0.5">
      <h4 className="text-sm font-medium mb-2">Document Metadata</h4>
      <MetadataRow label="Order Ref" value={orderId} copyable icon={FileText} onCopy={handleCopy} copiedField={copiedField} />
      <MetadataRow label="Service Code" value={serviceCode} copyable onCopy={handleCopy} copiedField={copiedField} />
      <MetadataRow label="Version" value={`v${version?.version}`} />
      <MetadataRow label="Status" value={version?.status} />
      <MetadataRow label="Generated At" value={formatDate(version?.generated_at || version?.created_at)} icon={Clock} />
      <MetadataRow label="Generated By" value={version?.generated_by || 'System'} icon={User} />
      {version?.prompt_version_used != null && (
        <MetadataRow
          label="Prompt version"
          value={formatPromptVersion(version.prompt_version_used)}
          copyable
          onCopy={handleCopy}
          copiedField={copiedField}
          icon={FileText}
        />
      )}
      {version?.sha256_hash && (
        <MetadataRow 
          label="SHA256 Hash" 
          value={`${version.sha256_hash.substring(0, 16)}...`} 
          copyable 
          icon={Hash}
          onCopy={handleCopy}
          copiedField={copiedField}
        />
      )}
      {version?.intake_snapshot_hash && (
        <MetadataRow 
          label="Intake Hash" 
          value={`${version.intake_snapshot_hash.substring(0, 16)}...`} 
          copyable
          onCopy={handleCopy}
          copiedField={copiedField}
        />
      )}
    </div>
  );
};

/**
 * Version history list
 */
const VersionHistory = ({ versions, selectedVersion, onVersionSelect }) => {
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium">Version History</h4>
      <ScrollArea className="h-[200px]">
        <div className="space-y-2 pr-2">
          {versions.map((v) => (
            <div
              key={v.version}
              data-testid={`version-item-${v.version}`}
              onClick={() => onVersionSelect(v)}
              className={cn(
                'p-2 rounded-lg border cursor-pointer transition-all',
                selectedVersion?.version === v.version
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300',
                v.status === 'SUPERSEDED' && 'opacity-60'
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-gray-500" />
                  <span className="font-medium text-sm">v{v.version}</span>
                  <VersionStatusBadge status={v.status} />
                  {v.is_approved && (
                    <Badge className="bg-green-100 text-green-800 text-xs">
                      <Lock className="h-3 w-3 mr-1" />
                      Approved
                    </Badge>
                  )}
                </div>
                <span className="text-xs text-gray-500">
                  {formatDate(v.generated_at || v.created_at)}
                </span>
              </div>
              {v.is_regeneration && (
                <p className="text-xs text-blue-600 mt-1">Regeneration</p>
              )}
              {v.regeneration_notes && (
                <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                  {v.regeneration_notes}
                </p>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

/**
 * Main DocumentPreviewModal component
 */
const DocumentPreviewModal = ({
  isOpen,
  onClose,
  orderId,
  serviceCode,
  versions = [],
  selectedVersion,
  onVersionSelect,
  onApprove,
  onRequestRegen,
  onRequestInfo,
  isSubmitting = false,
  isLocked = false,
}) => {
  const [hasReviewed, setHasReviewed] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const getTokenizedPreviewUrl = useCallback(
    async (format = 'pdf') => {
      const token = localStorage.getItem('auth_token');
      if (!token || !selectedVersion || !orderId) {
        throw new Error('Missing auth/session context');
      }
      const response = await fetch(
        `${API_URL}/api/admin/orders/${orderId}/documents/${selectedVersion.version}/token?format=${format}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        let detail = 'Failed to get document access token';
        try {
          const data = await response.json();
          detail = data?.detail || detail;
        } catch (_) {
          // ignore json parse failure
        }
        throw new Error(detail);
      }
      const data = await response.json();
      if (!data?.preview_url) {
        throw new Error('Invalid token response');
      }
      return data.preview_url;
    },
    [selectedVersion, orderId]
  );

  // Fetch token-based preview URL when version changes
  useEffect(() => {
    setHasReviewed(false);
    setPreviewUrl(null);
    setPreviewError('');

    if (!selectedVersion || !orderId) return;

    let mounted = true;

    const fetchPreviewToken = async () => {
      setIsLoadingPreview(true);
      try {
        const url = await getTokenizedPreviewUrl('pdf');
        if (mounted) {
          setPreviewUrl(url);
          setPreviewError('');
        }
      } catch (error) {
        console.error('Error fetching preview token:', error);
        if (mounted) {
          setPreviewUrl(null);
          const msg = error?.message || 'Unable to load document preview';
          setPreviewError(msg);
          toast.error(msg);
        }
      } finally {
        if (mounted) {
          setIsLoadingPreview(false);
        }
      }
    };

    fetchPreviewToken();

    return () => {
      mounted = false;
    };
  }, [selectedVersion, orderId, getTokenizedPreviewUrl]);

  const handleApprove = () => {
    if (!hasReviewed) {
      toast.error('Please confirm you have reviewed this document');
      return;
    }
    onApprove(selectedVersion);
  };

  const disabledReason = (() => {
    if (isSubmitting) return 'Action in progress. Please wait.';
    if (!selectedVersion) return 'No document version selected.';
    if (isLocked) return 'Document is already locked as final.';
    if (!hasReviewed) return 'Confirm review to enable approval.';
    return '';
  })();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto" data-testid="document-preview-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Document Preview
            {selectedVersion && (
              <>
                <span className="text-gray-400">-</span>
                <span className="font-mono">v{selectedVersion.version}</span>
                <VersionStatusBadge status={selectedVersion.status} />
              </>
            )}
            {selectedVersion?.is_approved && (
              <Badge className="bg-green-100 text-green-800">
                <Lock className="h-3 w-3 mr-1" />
                Final
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left sidebar - Metadata and versions */}
          <div className="space-y-4">
            <DocumentMetadata
              version={selectedVersion}
              orderId={orderId}
              serviceCode={serviceCode}
            />
            
            <Separator />
            
            <VersionHistory
              versions={versions}
              selectedVersion={selectedVersion}
              onVersionSelect={onVersionSelect}
            />
          </div>

          {/* Main content - Document preview */}
          <div className="lg:col-span-2 space-y-4">
            {/* PDF Preview */}
            <div 
              className="border rounded-lg bg-gray-100 overflow-hidden" 
              style={{ height: 'min(55vh, 560px)' }}
            >
              {isLoadingPreview ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-gray-500">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 mx-auto mb-2"></div>
                    <p>Loading document preview...</p>
                  </div>
                </div>
              ) : previewUrl ? (
                <iframe
                  src={previewUrl}
                  className="w-full h-full"
                  title={`Document Preview v${selectedVersion?.version}`}
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-gray-500">
                    <FileText className="h-12 w-12 mx-auto mb-2 opacity-40" />
                    <p className="font-medium">Preview unavailable</p>
                    <p className="text-sm mt-1 max-w-md">
                      {previewError || 'Document version not selected or preview could not be generated.'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Regeneration notes */}
            {selectedVersion?.regeneration_notes && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800 font-medium">Regeneration Notes:</p>
                <p className="text-sm text-blue-700">{selectedVersion.regeneration_notes}</p>
              </div>
            )}

            {/* Review gate checkbox */}
            {!isLocked && selectedVersion && (
              <div className="flex items-center space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <Checkbox
                  id="review-confirmation"
                  checked={hasReviewed}
                  onCheckedChange={setHasReviewed}
                  data-testid="review-checkbox"
                />
                <Label
                  htmlFor="review-confirmation"
                  className="text-sm font-medium text-amber-800 cursor-pointer"
                >
                  I have reviewed this document and confirm it is ready for approval
                </Label>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {/* Download buttons */}
          <div className="flex gap-2 flex-1">
            {selectedVersion?.filename_docx && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      onClick={async () => {
                        try {
                          const url = await getTokenizedPreviewUrl('docx');
                          window.open(url, '_blank', 'noopener,noreferrer');
                        } catch (e) {
                          toast.error(e?.message || 'Failed to generate DOCX download link');
                        }
                      }}
                      data-testid="download-docx-btn"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      DOCX (Editable)
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Download editable Word document</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {selectedVersion?.filename_pdf && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      onClick={async () => {
                        try {
                          const url = await getTokenizedPreviewUrl('pdf');
                          window.open(url, '_blank', 'noopener,noreferrer');
                        } catch (e) {
                          toast.error(e?.message || 'Failed to generate PDF download link');
                        }
                      }}
                      data-testid="download-pdf-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      PDF (Delivery)
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Download sealed PDF for client delivery</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

          {/* Action buttons (only if not locked) */}
          {!isLocked && selectedVersion && (
            <>
              <Button
                variant="outline"
                onClick={() => onRequestInfo()}
                disabled={isSubmitting}
                data-testid="request-info-btn"
              >
                Request Client Info
              </Button>
              <Button
                variant="outline"
                onClick={() => onRequestRegen()}
                disabled={isSubmitting}
                data-testid="request-regen-btn"
              >
                Request Revision
              </Button>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={handleApprove}
                      disabled={isSubmitting || !hasReviewed}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="approve-btn"
                    >
                      {!hasReviewed && <AlertCircle className="h-4 w-4 mr-2" />}
                      <Lock className="h-4 w-4 mr-2" />
                      Approve & Finalize
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {hasReviewed
                      ? 'Lock document as FINAL and proceed to delivery'
                      : 'You must confirm document review first'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </>
          )}

          {!isLocked && (
            <div className="w-full sm:w-auto text-xs text-amber-700 self-center">
              {disabledReason}
            </div>
          )}

          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { DocumentPreviewModal, DocumentMetadata, VersionHistory, VersionStatusBadge };
export default DocumentPreviewModal;
